import {
  AlertCircle,
  CheckCircle2,
  Clock,
  Copy,
  Loader2,
  RotateCcw,
  X,
} from "lucide-react";
import { formatFileSize } from "@/lib/utils";

// Máquina de estados por fichero:
// en_cola → subiendo → procesando → completado | error | duplicado
export type UploadState =
  | "en_cola"
  | "subiendo"
  | "procesando"
  | "completado"
  | "error"
  | "duplicado";

export interface QueueItem {
  key: string;
  file: File;
  state: UploadState;
  documentId?: string;
  error?: string;
}

export const TERMINAL_UPLOAD_STATES: UploadState[] = [
  "completado",
  "error",
  "duplicado",
];

const stateLabel: Record<UploadState, string> = {
  en_cola: "En cola",
  subiendo: "Subiendo…",
  procesando: "Procesando…",
  completado: "Completado",
  error: "Error",
  duplicado: "Duplicado",
};

function StateIcon({ state }: { state: UploadState }) {
  switch (state) {
    case "en_cola":
      return <Clock className="w-4 h-4 text-content-muted" />;
    case "subiendo":
    case "procesando":
      return <Loader2 className="w-4 h-4 text-brand animate-spin" />;
    case "completado":
      return <CheckCircle2 className="w-4 h-4 text-success" />;
    case "error":
      return <AlertCircle className="w-4 h-4 text-danger" />;
    case "duplicado":
      return <Copy className="w-4 h-4 text-warning" />;
  }
}

interface DocumentUploadQueueProps {
  items: QueueItem[];
  onRetry: (key: string) => void;
  onClear: () => void;
}

export function DocumentUploadQueue({
  items,
  onRetry,
  onClear,
}: DocumentUploadQueueProps) {
  if (items.length === 0) return null;

  const done = items.filter((i) =>
    TERMINAL_UPLOAD_STATES.includes(i.state)
  ).length;
  const total = items.length;
  const allDone = done === total;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="bg-surface border border-line rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-heading font-semibold text-ink">
              {done}/{total} procesados
            </p>
            {allDone && (
              <button
                onClick={onClear}
                className="inline-flex items-center gap-1 text-xs text-content-muted hover:text-ink"
              >
                <X className="w-3.5 h-3.5" />
                Limpiar
              </button>
            )}
          </div>
          <div className="w-full bg-surface-200 rounded-full h-2">
            <div
              className="bg-brand h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>

      <ul className="space-y-1.5 max-h-56 overflow-y-auto">
        {items.map((item) => (
          <li
            key={item.key}
            className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-surface-100"
          >
            <StateIcon state={item.state} />
            <span className="flex-1 min-w-0 truncate text-content">
              {item.file.name}
            </span>
            <span className="text-xs text-content-muted hidden sm:inline">
              {formatFileSize(item.file.size)}
            </span>
            <span
              className={`text-xs ${
                item.state === "error"
                  ? "text-danger"
                  : item.state === "duplicado"
                    ? "text-warning"
                    : "text-content-muted"
              }`}
              title={item.error}
            >
              {item.state === "error" && item.error
                ? item.error.length > 60
                  ? `${item.error.slice(0, 60)}…`
                  : item.error
                : stateLabel[item.state]}
            </span>
            {item.state === "error" && (
              <button
                onClick={() => onRetry(item.key)}
                className="inline-flex items-center gap-1 text-xs text-brand hover:text-brand-hover font-medium flex-shrink-0"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reintentar
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
